/**
 * Lite Square 入口：先以 LINE 登入，登入後到選擇頁（新建 / 進入已有 Square）
 * 優先使用 LIFF（OA／連結進入）；非 LIFF 環境 fallback 至後端 LINE Login。
 * 本機測試：可開啟 ?mock=1 或於 DEV 時使用「Mock 登入」跳過 LINE OAuth（後端須為 dev 且接受 mock_token_）
 */

import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { getApiBase } from '../../api/apiUtils';
import { registerNewUser } from '../../api/contextClient';
import { setLiteJwtToken } from '../../api/liteAuthToken';
import { useLiff } from '../../contexts/LiffContext';

function base64UrlEncodeUtf8(str: string): string {
  const bytes = new TextEncoder().encode(str);
  let binary = '';
  for (let i = 0; i < bytes.length; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
}

function base64UrlEncodeState(state: {
  redirect_uri: string;
  mode: string;
}): string {
  const json = JSON.stringify(state);
  return base64UrlEncodeUtf8(json);
}

/** 是否顯示 Mock 登入（本機測試用）：DEV 或 URL 帶 ?mock=1 */
function shouldShowMockSquareLogin(): boolean {
  if (typeof window === 'undefined') return false;
  if (import.meta.env.DEV) return true;
  return new URLSearchParams(window.location.search).get('mock') === '1';
}

export function NewSquarePage() {
  const navigate = useNavigate();
  const { liff, isReady, isLoggedIn, shouldUseLiff } = useLiff();
  const [error, setError] = useState<string | null>(null);
  const [redirecting, setRedirecting] = useState(false);
  const [liffRegistering, setLiffRegistering] = useState(false);
  const [mockLoading, setMockLoading] = useState(false);
  const hasAutoRegisteredRef = useRef(false);

  /** LIFF 已登入時：取得 ID Token → register → 存 JWT → 導向 square-choice */
  const registerWithLiffAndNavigate = async () => {
    if (!liff || !isLoggedIn) return;
    setError(null);
    setLiffRegistering(true);
    try {
      const lineIdToken = await liff.getIDToken();
      if (!lineIdToken) {
        setError('無法取得 LINE 認證');
        return;
      }
      const result = await registerNewUser({ lineIdToken });
      if (result.jwtToken) {
        try {
          setLiteJwtToken(result.jwtToken);
        } catch {
          /* localStorage 可能被停用或已滿，忽略 */
        }
        navigate('/lite/square-choice', { replace: true });
      } else {
        setError('無法取得登入憑證');
      }
    } catch (err) {
      const message =
        err instanceof Error ? err.message : '登入失敗，請稍後再試';
      setError(message);
    } finally {
      setLiffRegistering(false);
    }
  };

  // LIFF 已就緒且已登入時，自動執行 register 並導向（避免多點一次按鈕）；僅執行一次，不將 registerWithLiffAndNavigate 納入 deps
  useEffect(() => {
    if (
      !shouldUseLiff ||
      !isReady ||
      !liff ||
      !isLoggedIn ||
      hasAutoRegisteredRef.current
    ) {
      return;
    }
    hasAutoRegisteredRef.current = true;
    registerWithLiffAndNavigate();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- 僅在 LIFF 就緒且已登入時執行一次
  }, [shouldUseLiff, isReady, liff, isLoggedIn]);

  /** 使用 LINE 登入按鈕：LIFF 時呼叫 liff.login，否則 fallback 後端 LINE Login */
  const handleLogin = () => {
    setError(null);
    if (shouldUseLiff && liff) {
      const redirectUri =
        window.location.origin +
        window.location.pathname +
        window.location.search;
      liff.login({ redirectUri });
      setRedirecting(true);
      return;
    }
    try {
      const apiBase = getApiBase();
      const redirectUri = `${window.location.origin}/lite/square-choice`;
      const state = base64UrlEncodeState({
        redirect_uri: redirectUri,
        mode: 'choice',
      });
      const url = `${apiBase}/api/lite/auth/line?redirect_uri=${encodeURIComponent(redirectUri)}&state=${encodeURIComponent(state)}`;
      if (import.meta.env.DEV) {
        console.log('[NewSquarePage] 導向後端 LINE 登入（fallback）:', url);
      }
      setRedirecting(true);
      window.location.href = url;
    } catch (err) {
      const message = err instanceof Error ? err.message : '無法取得 API 位址';
      setError(message);
    }
  };

  /** Mock 登入：用 mock_token_ 呼叫 POST /lite/auth/register，取得 JWT 後導向 square-choice（僅本機測試） */
  const handleMockLogin = async () => {
    setError(null);
    setMockLoading(true);
    try {
      const lineIdToken = `mock_token_Usquare_mock_${Date.now()}`;
      const result = await registerNewUser({ lineIdToken });
      if (result.jwtToken) {
        try {
          setLiteJwtToken(result.jwtToken);
        } catch {
          /* localStorage 可能被停用或已滿，忽略 */
        }
        navigate('/lite/square-choice', { replace: true });
        return;
      }
      setError('無法取得 Mock 登入憑證');
    } catch (err) {
      const message =
        err instanceof Error
          ? err.message
          : 'Mock 登入失敗（請確認後端為 dev 且已啟動）';
      setError(message);
    } finally {
      setMockLoading(false);
    }
  };

  if (redirecting) {
    return (
      <div className="min-h-[100dvh] bg-gradient-to-br from-slate-50 to-slate-100 flex flex-col items-center justify-center p-4">
        <p className="text-slate-600">正在導向至 LINE 登入…</p>
      </div>
    );
  }

  if (shouldUseLiff && !isReady) {
    return (
      <div className="min-h-[100dvh] bg-gradient-to-br from-slate-50 to-slate-100 flex flex-col items-center justify-center p-4">
        <p className="text-slate-600">載入中…</p>
      </div>
    );
  }

  // OA 內 LIFF 已登入時：後台自動 register → 導向商家一覽，只顯示「登入中…」不秀「商家一覽」卡片，避免多餘步驟
  if (shouldUseLiff && isLoggedIn && liffRegistering) {
    return (
      <div className="min-h-[100dvh] bg-gradient-to-br from-slate-50 to-slate-100 flex flex-col items-center justify-center p-4">
        <p className="text-slate-600">登入中…</p>
      </div>
    );
  }

  return (
    <div className="min-h-[100dvh] bg-gradient-to-br from-slate-50 to-slate-100 flex flex-col items-center justify-center p-4">
      <div className="w-full max-w-sm rounded-2xl bg-white shadow-lg p-6">
        <h1 className="text-xl font-semibold text-slate-800 text-center mb-2">
          商家一覽
        </h1>
        <p className="text-sm text-slate-500 text-center mb-6">
          使用 LINE 登入後，可新建商家或進入已有商家
        </p>
        <button
          type="button"
          onClick={handleLogin}
          disabled={liffRegistering}
          className="w-full rounded-lg bg-[#06C755] text-white font-medium py-3 hover:opacity-90 transition disabled:opacity-50"
        >
          {liffRegistering ? '登入中…' : '使用 LINE 登入'}
        </button>
        {shouldShowMockSquareLogin() && (
          <>
            <button
              type="button"
              onClick={handleMockLogin}
              disabled={mockLoading}
              className="w-full mt-3 rounded-lg border border-slate-300 text-slate-600 font-medium py-3 hover:bg-slate-50 disabled:opacity-50"
            >
              {mockLoading ? 'Mock 登入中…' : 'Mock 登入（本機測試）'}
            </button>
            <p className="mt-2 text-xs text-slate-400 text-center">
              需後端以 dev 啟動（接受 mock_token_）
            </p>
          </>
        )}
        {error && (
          <p className="mt-4 text-sm text-red-600 text-center" role="alert">
            {error}
          </p>
        )}
      </div>
    </div>
  );
}
